import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const PinScreen = ({ userId, onSuccess }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [intentosRestantes, setIntentosRestantes] = useState(3);
  const [cooldown, setCooldown] = useState(0);

  // Detección de bypass al montar
  useEffect(() => {
    const bypassed = sessionStorage.getItem('pinBypassed');
    if (bypassed === 'true') {
      sessionStorage.removeItem('pinBypassed');
      onSuccess();
    }
  }, [onSuccess]);

  // Auto-focus en el input
  useEffect(() => {
    if (inputRef.current && cooldown === 0) {
      inputRef.current.focus();
    }
  }, [cooldown]);

  // Temporizador de cooldown
  useEffect(() => {
    if (cooldown > 0) {
      const interval = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setError('');
            setIntentosRestantes(3);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [cooldown]);

  // Auto-submit cuando se completan 3 caracteres
  useEffect(() => {
    if (pin.length === 3 && !loading && cooldown === 0) {
      handleSubmit({ preventDefault: () => {} });
    }
  }, [pin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cooldown > 0) return;
    if (pin.length !== 3) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`/api/usuarios/${userId}/pin/verificar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pin })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // PIN correcto
        onSuccess();
      } else if (response.status === 429) {
        // Cooldown activo
        setCooldown(data.tiempoRestante || 300);
        setError(data.message || 'Demasiados intentos. Espera 5 minutos.');
        setPin('');
      } else {
        // PIN incorrecto
        const nuevosIntentos = intentosRestantes - 1;
        setIntentosRestantes(nuevosIntentos);
        
        if (nuevosIntentos <= 0) {
          setCooldown(300); // 5 minutos
          setError('Demasiados intentos. Espera 5 minutos.');
        } else {
          setError(`PIN incorrecto. Te quedan ${nuevosIntentos} intento${nuevosIntentos > 1 ? 's' : ''}.`);
        }
        
        setPin('');
      }
    } catch (error) {
      console.error('Error verificando PIN:', error);
      setError('Error de conexión. Intenta de nuevo.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value;
    // Solo alfanuméricos, máximo 3 caracteres
    if (/^[A-Za-z0-9]{0,3}$/.test(value)) { 
      setPin(value);
      setError('');
    }
  };

  const handleOlvideMiPin = () => {
    navigate('/login');
  };

  const formatTiempo = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--gray-50)',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '400px',
        width: '100%',
        backgroundColor: 'var(--white)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        padding: '40px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--yellow-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <i className="bi bi-shield-lock-fill" 
               style={{
                 fontSize: '32px',
                 color: 'var(--yellow-600)'
               }}></i>
          </div>
          <h2 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 600,
            color: 'var(--gray-900)',
            marginBottom: '8px'
          }}>
            Ingresa tu PIN
          </h2>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--gray-600)'
          }}>
            Protege tu información con tu PIN de 3 caracteres
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <input
              ref={inputRef}
              type="text"
              value={pin}
              onChange={handlePinChange}
              maxLength={3}
              disabled={loading || cooldown > 0}
              placeholder="●●●"
              autoComplete="off"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '20px',
                fontWeight: 600,
                textAlign: 'center',
                letterSpacing: '8px',
                border: error ? '2px solid var(--red-500)' : '2px solid var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                transition: 'var(--transition-base)',
                backgroundColor: cooldown > 0 ? 'var(--gray-100)' : 'var(--white)',
                cursor: cooldown > 0 ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => {
                if (cooldown === 0) {
                  e.target.style.borderColor = 'var(--yellow-500)';
                }
              }}
              onBlur={(e) => {
                if (!error) {
                  e.target.style.borderColor = 'var(--gray-300)';
                }
              }}
            />
          </div>

          {/* Contador de intentos */}
          {cooldown === 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: 'var(--radius-full)',
                    backgroundColor: index < intentosRestantes 
                      ? 'var(--yellow-500)' 
                      : 'var(--gray-300)',
                    transition: 'var(--transition-base)'
                  }}
                />
              ))}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: 'var(--red-50)',
              border: '1px solid var(--red-200)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '16px'
            }}>
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--red-700)',
                textAlign: 'center',
                margin: 0
              }}>
                {error}
              </p>
            </div>
          )}

          {/* Temporizador de cooldown */}
          {cooldown > 0 && (
            <div style={{
              padding: '16px',
              backgroundColor: 'var(--yellow-50)',
              border: '1px solid var(--yellow-200)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--yellow-700)',
                marginBottom: '8px',
                fontFamily: 'monospace'
              }}>
                {formatTiempo(cooldown)}
              </div>
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--yellow-700)',
                margin: 0
              }}>
                Podrás intentar de nuevo cuando termine el tiempo
              </p>
            </div>
          )}

          {/* Loading indicator */}
          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '12px',
              color: 'var(--gray-600)',
              fontSize: 'var(--font-size-sm)'
            }}>
              Verificando...
            </div>
          )}

          {/* Botón olvidé mi PIN */}
          <button
            type="button"
            onClick={handleOlvideMiPin}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--yellow-600)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-base)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--yellow-50)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ¿Olvidaste tu PIN? Solicita un magic link
          </button>
        </form>

        {/* Footer info */}
        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid var(--gray-200)',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--gray-500)',
            margin: 0
          }}>
            <i className="bi bi-info-circle" style={{ marginRight: '4px' }}></i>
            Tu PIN protege el acceso desde tu tag NFC
          </p>
        </div>
      </div>
    </div>
  );
};

export default PinScreen;